(ns storrito.connect3.common
  (:require [clojure.string :as str]
            [storrito.calendly.links :as calendly]
            [storrito.connect3.device :as device]
            [storrito.instagram.proxy-error :as proxy-error]
            [storrito.util.country-code :as country-code]
            [storrito.util.link :as link]))

(defn device-label
  "The device Storrito presents to Instagram, as its login request names
   it: the account's own on a reconnect (`fetch-device-label!` of
   `storrito.connect3.core`), else the one of a new connect."
  [state]
  (or (:device-label state)
      device/default-label))

(defn location-label
  "Human-readable label for the connect location, e.g. \"Berlin, Germany\"."
  [state]
  (let [country (or (get country-code/code->country-name (:country state))
                    (:country state))
        city    (:city state)]
    (cond
      (and city country) (str city ", " country)
      country            country
      city               city)))

(defn instagram-logo []
  [:div.flex.mb-2.justify-center
   [:img {:width "200px"
          :src "/img/instagram-new-logo.png"}]])

(def login-data-keys
  "What the login panel sends with the password (its Login button), and
   what the code login re-sends together with `:recovery` (see
   `storrito.connect3.core/recovery-login-data`)."
  [:username
   :password
   :reconnect
   :proxy
   :connection-method
   :country
   :city
   :latitude
   :longitude])

(defn edge-whitespace?
  "True when a password starts or ends with whitespace. Instagram allows
   that, but it is almost always a paste or autofill artifact: of 557
   password submissions in 31.08.-03.09.2026, 8 had one and Instagram
   rejected all 8. The password is sent as typed; connect.php retries
   once with the whitespace stripped when Instagram rejects it, and the
   login panel shows a hint."
  [password]
  (boolean (and (string? password)
                (not= password (str/trim password)))))

(defn login-data
  "What the login panel sends: `login-data-keys` of the state with the
   username trimmed - Instagram usernames cannot contain spaces, so a
   pasted one is an artifact. The password goes out as typed."
  [state]
  (cond-> (select-keys state login-data-keys)
    (string? (:username state)) (update :username str/trim)))

(defn code-login-card
  "Offers the code login (handleRecovery.php): Instagram sends a login
   code to a contact point of the account, no password needed. Shown as
   step `number` of the hints below a rejected password."
  [number]
  [:div.rounded-lg.border.border-blue-200.bg-blue-50.p-3
   [:div.flex.items-start.gap-2.5
    [:div.flex-shrink-0.w-6.h-6.rounded-full.bg-blue-600.text-white.flex.items-center.justify-center.text-xs.font-bold
     (str number)]
    [:div.flex-1
     [:p.font-semibold.text-sm.text-gray-900.mb-1 "Log in with a code instead"]
     [:p.text-xs.text-gray-700.mb-3
      "Instagram sends a login code to the email address or phone number
       of this account — no password needed."]
     [:button.inline-flex.items-center.justify-center.bg-blue-600.text-white.px-4.py-2.rounded-md.font-medium.text-sm.hover:bg-blue-700.transition.cursor-pointer
      {:type "button"
       :on {:click [[:connect3/start-recovery]]}}
      [:i.fas.fa-key.mr-2]
      "Send me a login code"]]]])

(defn- email-link-card
  [number]
  [:div.rounded-lg.border.border-gray-200.bg-white.p-3
   [:div.flex.items-start.gap-2.5
    [:div.flex-shrink-0.w-6.h-6.rounded-full.bg-gray-100.text-gray-700.flex.items-center.justify-center.text-xs.font-bold
     (str number)]
    [:div.flex-1
     [:p.font-semibold.text-sm.text-gray-900.mb-1 "Connect via email link"]
     [:p.text-xs.text-gray-700.mb-3
      "Instagram may have emailed you a "
      [:b "“Log in as”"]
      " link after the failed attempt — or you can request one via "
      [:a.underline.text-blue-600
       {:href "https://www.instagram.com/accounts/password/reset/"
        :target "_blank"}
       "Instagram's password reset page"]
      ". Paste that link into Storrito instead of using a password ("
      [:a.underline.text-blue-600
       {:href (link/help-center-url "e29264da-fe60-410c-9152-9bd2b07bdebb")
        :target "_blank"}
       "learn more"]
      ")."]
     [:button.inline-flex.items-center.justify-center.border.border-gray-300.bg-white.text-gray-700.px-4.py-2.rounded-md.font-medium.text-sm.hover:bg-gray-50.transition.cursor-pointer
      {:type "button"
       :on {:click [[:store/assoc-in [:connect3 :email-connect?] true]
                    [:store/assoc-in [:connect3 :error-message] nil]]}}
      [:i.fas.fa-envelope.mr-2]
      "Use email link instead"]]]])

(defn call-offer
  "The \"book a call\" line under a connect error. `utm-source` names
   the error placement for the Calendly click attribution, `org-uuid`
   joins the booking to the organization."
  [utm-source org-uuid]
  [:p.text-xs.text-gray-600
   "Prefer to sort it out together? "
   [:a.underline.text-blue-600.font-medium
    {:href (calendly/url :connect-help utm-source org-uuid)
     :target "_blank"}
    "Book a free 15-minute call with Lydia"]
   "."])

(defn wrong-password-message
  ;; Instagram rejects some correct passwords depending on the exit IP,
  ;; and every re-submit leaves over a fresh proxy exit (see
  ;; storrito.connect3.instagram-client/working-proxy-url). 16.–23.09.2026:
  ;; a second try with the identical password logged in 6 of 25 sessions,
  ;; with a changed password 12 of 51 - about 1 in 4 either way - while
  ;; 26 of 102 sessions with this message never tried again (0 logins).
  ;; So the first hint asks for one more try before doubting the password.
  [org-uuid]
  [:div.flex.flex-col.gap-3.text-left
   [:div.mt-5
    [:b.text-red-500.block "Instagram rejected this password."]
    [:span.text-sm.text-red-500 "You have three options:"]]

   [:div.rounded-lg.border.border-gray-200.bg-white.p-3
    [:div.flex.items-start.gap-2.5
     [:div.flex-shrink-0.w-6.h-6.rounded-full.bg-gray-100.text-gray-700.flex.items-center.justify-center.text-xs.font-bold "1"]
     [:div.flex-1
      [:p.font-semibold.text-sm.text-gray-900.mb-1 "Try once more, then check the password"]
      [:p.text-xs.text-gray-600
       "Instagram sometimes rejects a correct password — Storrito connects
        over a different connection on every try, and about 1 in 4 second
        tries goes through. Click Login again. If it fails again, double-check
        the password, "
       [:a.underline.text-blue-600
        {:href "https://help.instagram.com/566810106808145/?helpref=uf_share"
         :target "_blank"}
        "activate 2FA on your Instagram account"]
       ", or change your Instagram password in the browser or the Instagram app and try again."]]]]

   (code-login-card 2)

   (email-link-card 3)

   (call-offer "connect-error-password" org-uuid)])

(def authentication-confirmation-message
  [:div.flex.flex-col.gap-3.text-left
   [:div.mt-5
    [:b.text-red-500.block "Instagram wants to confirm it's really you."]
    ;; "Re-entering the same password will not help" was wrong: the retry
    ;; leaves over a fresh proxy exit and logged in 8 of 35 sessions
    ;; (16.–23.09.2026); 19 of 65 sessions with this message never tried
    ;; anything again.
    [:span.text-sm.text-red-500
     "Your password is probably correct — Instagram is asking for an extra
      confirmation instead of accepting this login. Clicking Login once more
      works in about 1 in 4 cases (Storrito uses a different connection each
      time); otherwise use one of these:"]]

   (code-login-card 1)

   (email-link-card 2)

   [:div.rounded-lg.border.border-gray-200.bg-white.p-3
    [:div.flex.items-start.gap-2.5
     [:div.flex-shrink-0.w-6.h-6.rounded-full.bg-gray-100.text-gray-700.flex.items-center.justify-center.text-xs.font-bold "3"]
     [:div.flex-1
      [:p.font-semibold.text-sm.text-gray-900.mb-1 "Or confirm it in the Instagram app"]
      [:p.text-xs.text-gray-600
       "Open Instagram on your phone, approve any "
       [:b "“Was this you?”"]
       " security notification, then try connecting again."]]]]])

(defn login-link-sent-message
  "Instagram's authentication_confirmation answer when it names where it
   sent a login link instead (`:login-link-sent-to`, see loginLink.php):
   \"The password you entered is incorrect. To log in, use the link we
   sent to +39 *** *** **92.\" Seen 2026-10-21: an agency retried a
   client's outdated password 9 times while every link went to the
   client's phone, and the generic hint called the password \"probably
   correct\"."
  [contact-point org-uuid]
  [:div.flex.flex-col.gap-3.text-left
   [:div.mt-5
    [:b.text-red-500.block "Instagram rejected this password and sent a login link instead."]
    [:span.text-sm.text-red-500
     "Instagram sent the link to " [:b contact-point] "."]]

   [:div.rounded-lg.border.border-gray-200.bg-white.p-3
    [:div.flex.items-start.gap-2.5
     [:div.flex-shrink-0.w-6.h-6.rounded-full.bg-gray-100.text-gray-700.flex.items-center.justify-center.text-xs.font-bold "1"]
     [:div.flex-1
      [:p.font-semibold.text-sm.text-gray-900.mb-1 "Check that the password is current"]
      ;; Unlike the other rejected-password hints no "try once more":
      ;; Instagram itself calls the password incorrect here, and the retries
      ;; of 2026-10-21 over fresh exit IPs all failed again.
      [:p.text-xs.text-gray-600
       "If this account belongs to someone else, e.g. a client, ask the
        person with " [:b contact-point]
       " for the current password, or let them connect the account
        themselves. Please don't retry the same password: repeated attempts
        can make Instagram block logins to this account for a while."]]]]

   (code-login-card 2)

   (email-link-card 3)

   (call-offer "connect-error-login-link" org-uuid)])

(def not-supported-challenge-type-message
  [:div.flex.flex-col.gap-2
   [:b "Please choose a different 2FA method in Instagram and try again."]
   [:a.underline.font-medium
    {:href (link/help-center-url "f5de5ee8-b0c5-4074-8a5c-769f29a882f6")
     :target "_blank"}
    "Also make sure Instagram app approval is disabled"]])

(def recently-changed-password-message
  [:div.flex.flex-col.gap-1
   [:b "Please check your password."]
   [:span "It looks like this Instagram password was changed recently. Use the new password, or reset it in Instagram and try again."]])

(def ^:private wrong-code-statuses
  #{"two-factor-code-wrong"
    "generic-two-factor-code-wrong"
    "recovery-code-wrong"})

(defn- includes-message?
  [message substring]
  (and (string? message)
       (str/includes? (str/lower-case message)
                      (str/lower-case substring))))

(defn- clean-technical-message
  [message]
  (when (string? message)
    (-> message
        (str/replace #"^Login did not complete successfully after handling:\s*" "")
        (str/replace #"^InstagramAPI\\(?:Response|Exception)\\[^:]+:\s*" "")
        (str/trim))))

(defn- technical-message?
  [message]
  (and (string? message)
       (or (str/includes? message "InstagramAPI\\")
           (str/includes? message "Exception"))))

(defn- local-code-validation-message?
  [message]
  (or (includes-message? message "2fa code must be")
      (includes-message? message "backup code must be")))

(defn authentication-confirmation?
  "True for Instagram's `authentication_confirmation` login challenge.

   The PHP library surfaces it as an IncorrectPasswordException with this
   message even though the password is usually correct — Instagram just
   distrusts the login context and wants a verification-code confirmation.
   It must not be presented as a wrong password."
  [state]
  (and (= (:class state)
          "InstagramAPI\\Exception\\IncorrectPasswordException")
       (includes-message? (:error-message state)
                          "you can login via verification code")))

(defn- used-or-expired-login-link?
  "Instagram's answer to an email connect with a login link that was
   already used or is older than 3 days (connect.php asks for a new link)."
  [message]
  (includes-message? message "link is one time use"))

(defn- raw-message->friendly-message
  [message]
  (let [clean-message (clean-technical-message message)]
    (cond
      (not (seq clean-message))
      nil

      (= clean-message "FIRST_PASSWORD_FAILURE")
      "It looks like this Instagram password was changed recently. Use the new password, or reset it in Instagram and try again."

      (includes-message? clean-message "valid 2fa challenge type")
      "Please choose a different 2FA method in Instagram and try again."

      (or (includes-message? clean-message "code you entered is wrong")
          (includes-message? clean-message "two-factor-code is wrong")
          (includes-message? clean-message "invalid2facode")
          (includes-message? clean-message "invalid 2fa"))
      "That verification code didn't work. Please use the newest code from Instagram and try again."

      (includes-message? clean-message "starting the instagram login took too long")
      "Instagram is taking longer than usual to respond. Please try again in a moment."

      (includes-message? clean-message "starting the instagram login failed")
      "We couldn't start the Instagram login. Please try again in a moment."

      (includes-message? clean-message "please wait a few minutes")
      "Instagram is asking us to slow down. Please wait a few minutes, then try again."

      (or (includes-message? clean-message "throttled by instagram")
          (includes-message? clean-message "too many api requests"))
      "Instagram is temporarily rate-limiting this login. Please wait a few minutes and try again."

      (or (includes-message? clean-message "challenge_required")
          (includes-message? clean-message "checkpoint")
          (includes-message? clean-message "feedback required"))
      "Instagram needs one more security check. Open Instagram in the app or browser, complete the check, then try connecting again."

      (includes-message? clean-message "login support v2")
      "Instagram needs you to finish an account recovery step before Storrito can connect. Open Instagram, follow their instructions, then try again."

      (or (includes-message? clean-message "network")
          (includes-message? clean-message "empty response"))
      "Instagram did not respond. Please try again in a moment."

      (includes-message? clean-message "internal iteration limit")
      "We tried this step several times, but Instagram kept returning the same challenge. Please restart the connection and try again."

      (includes-message? clean-message "account on moderation")
      "Instagram is reviewing this account. Open Instagram and resolve any account notices before trying again."

      (includes-message? clean-message "captcha")
      "Instagram asked for a CAPTCHA we couldn't complete in time. Open Instagram, complete any security prompt, then try again."

      (used-or-expired-login-link? clean-message)
      "Instagram's login links work only once and only for 3 days. Request a new email on the Instagram password reset page (step 2) and paste the link of its \"Log in as\" button here."

      (technical-message? clean-message)
      "Instagram returned an unexpected response. Please try again — if it keeps happening, contact support and we'll help."

      :else
      clean-message)))

(defn account-recovery-limbo?
  "True for Instagram's account-recovery screen
   (com.bloks.www.ap.last_resort_recovery.limbo): the bloks login answers
   it for a username Instagram no longer maps to a login-able account."
  [state]
  (and (= (:class state) "InstagramAPI\\Exception\\UnexpectedLoginErrorException")
       (includes-message? (:error-message state) "no longer connected to an account")))

(def identification-error-class
  "connect.php's class for Instagram's assistive login screen (\"Is this
   your account?\"): no account has the typed username, but one with the
   same password is a typo away. The message carries Instagram's
   suggestion as `:suggested-username` (see assistiveLogin.php)."
  "InstagramAPI\\Exception\\IdentificationErrorException")

(def instagram-recovery-wall-classes
  "Instagram walls that end the login (connect.php reports them as
   `unhandled-exception`): the account has to be verified or recovered in
   the Instagram app first, a retry from here gets the same wall.
   AccountVerificationRequired = \"Please login with your device or using
   web browser to recover your account.\", UpdateRiskyContactpoint =
   Instagram asks to review the phone number or email of the account,
   AccountConfirmationRequired = the \"Confirm your account\" wall the
   code login (handleRecovery.php) runs into for an account under review,
   AccountDisabled = \"Account has been suspended.\" (Instagram suspended
   or disabled the account, only an appeal at Instagram brings it back),
   UnknownTwoFactorScreen = a two-step verification screen the library
   cannot complete (instagram-php >= v25.8.21, an \"approve from another
   device\" step without approval data): the login has to be approved in
   the Instagram app first."
  #{"InstagramAPI\\Exception\\AccountVerificationRequiredException"
    "InstagramAPI\\Exception\\AccountDisabledException"
    "InstagramAPI\\Exception\\Checkpoint\\UpdateRiskyContactpointChallengeException"
    "InstagramAPI\\Exception\\UnknownTwoFactorScreenException"
    "Internal\\Exception\\AccountConfirmationRequiredException"})

(defn instagram-recovery-wall?
  [state]
  (contains? instagram-recovery-wall-classes (:class state)))

(defn- class-error-message
  [state]
  (case (:class state)
    "InstagramAPI\\Exception\\AccountVerificationRequiredException"
    "Instagram wants you to verify this account before it lets Storrito in. Open the Instagram app or instagram.com, log in to this account there and finish the steps Instagram shows. Then connect again here."

    "InstagramAPI\\Exception\\Checkpoint\\UpdateRiskyContactpointChallengeException"
    "Instagram asks you to review the phone number or email address of this account first. Open the Instagram app, log in to this account there and confirm or update the contact details Instagram shows. Then connect again here."

    "InstagramAPI\\Exception\\UnknownTwoFactorScreenException"
    "Instagram wants this login approved in a way Storrito can't complete from here. Open the Instagram app on a phone where this account is logged in, approve the login if Instagram asks, then connect again here."

    "InstagramAPI\\Exception\\AccountDisabledException"
    "Instagram has suspended or disabled this account, so nobody can log in to it right now. Open the Instagram app or instagram.com, log in to this account there and follow Instagram's steps to appeal. You can connect it again here once Instagram has restored the account."

    ;; Instagram also answers "We can't find an account with <username>"
    ;; for existing accounts that have no Instagram password of their own
    ;; (created from a Facebook Page or added via Meta's Accounts Center),
    ;; so checking the spelling alone does not help those users.
    "InstagramAPI\\Exception\\InvalidUserException"
    "We couldn't find this Instagram username. Please check the spelling. If it is correct, this account may not have its own Instagram password yet (e.g. it was created from a Facebook Page or added via Meta's Accounts Center). Set a password for it in the Instagram app under Settings → Accounts Center → Password and security, then try again."

    "InstagramAPI\\Exception\\InvalidUsernameException"
    "This doesn't look like a valid Instagram username. Please check it and try again."

    "InstagramAPI\\Exception\\IdentificationErrorException"
    "Instagram can't find an account with this username. Please check the spelling and try again."

    ;; connect.php reports the bloks login's error screens with this class
    ;; (the legacy login it used to fall back to no longer exists).
    "InstagramAPI\\Exception\\UnexpectedLoginErrorException"
    (if (account-recovery-limbo? state)
      "Instagram says this login info is no longer connected to an account. Please check the username. If it is correct, Instagram has moved the account into account recovery: open the Instagram app, log in there and finish the recovery steps, then try again."
      "Instagram returned an unexpected login error. Please try again in a moment — if it keeps happening, contact support and we'll help.")

    nil))

(defn- status-error-message
  [state]
  (cond
    (and (contains? wrong-code-statuses (:status state))
         (not (local-code-validation-message? (:error-message state))))
    "That verification code didn't work. Please use the newest code from Instagram and try again."

    (= (:status state) "network-exception")
    "Instagram did not respond. Please try again in a moment."

    (= (:status state) "iteration-limit-reached")
    ;; the 2fa code loops hit the limit when every submitted code was wrong,
    ;; and their error-message names the likely fix (a stale code source) -
    ;; that hint must survive the generic iteration-limit copy:
    (if (includes-message? (:error-message state) "correct source")
      "Please check if you are using the correct source for the two-factor codes, then restart the connection and try again."
      "We tried this step several times, but Instagram kept returning the same challenge. Please restart the connection and try again.")

    (= (:status state) "recaptcha-timeout")
    "Instagram asked for a CAPTCHA we couldn't complete in time. Open Instagram, complete any security prompt, then try again."))

(defn friendly-error-message
  [state]
  (or (proxy-error/friendly-error-message-short
        {:message (:error-message state)}
        (:proxy state))
      (status-error-message state)
      (class-error-message state)
      (raw-message->friendly-message (:error-message state))))

(defn- error-title
  [state]
  (cond
    (or (proxy-error/friendly-error-message-short
          {:message (:error-message state)}
          (:proxy state))
        (= (:status state) "network-exception")
        (includes-message? (:error-message state) "proxy")
        (includes-message? (:error-message state) "relay app"))
    "Connection problem"

    (local-code-validation-message? (:error-message state))
    "Check the code"

    (contains? wrong-code-statuses (:status state))
    "Incorrect verification code"

    (= (:class state) "InstagramAPI\\Exception\\IncorrectPasswordException")
    "Incorrect password"

    ;; handleRecovery.php: the code login is not available for this
    ;; account, the message says why (the "Confirm your account" wall ends
    ;; the session instead, see `instagram-recovery-wall-classes`)
    (= (:class state) "Internal\\Exception\\RecoveryUnavailableException")
    "No code login for this account"

    (#{"InstagramAPI\\Exception\\InvalidUserException"
       "InstagramAPI\\Exception\\InvalidUsernameException"
       identification-error-class}
     (:class state))
    "Check the username"

    (account-recovery-limbo? state)
    "Instagram is recovering this account"

    (instagram-recovery-wall? state)
    "Instagram needs an extra step for this account"

    (= (:class state) "InstagramAPI\\Exception\\UnexpectedLoginErrorException")
    "Instagram couldn't log in"

    (used-or-expired-login-link? (:error-message state))
    "This login link no longer works"

    :else
    "We couldn't complete this step"))

(defn- error-card
  [title body org-uuid]
  [:div.w-full.mt-3.rounded-xl.border.border-red-200.bg-red-50.p-4.text-left.shadow-sm
   [:div.flex.gap-3
    [:div.mt-0.5.flex.h-8.w-8.flex-shrink-0.items-center.justify-center.rounded-full.bg-red-100.text-red-600
     [:i.fas.fa-exclamation-circle]]
    [:div.flex-1
     [:p.text-sm.font-semibold.text-red-800 title]
     [:div.mt-1.text-sm.text-red-700 body]
     [:div.mt-2 (call-offer "connect-error" org-uuid)]]]])

(defn- username-suggestion
  "Instagram's suggestion after a username typo, with a button that puts
   it into the username field."
  [suggested-username]
  [:div
   [:p
    "Instagram can't find an account with this username, but suggests "
    [:b (str "@" suggested-username)]
    ". If that is your account, use it and log in again."]
   [:button.mt-3.inline-flex.items-center.justify-center.border.border-gray-300.bg-white.text-gray-700.px-4.py-2.rounded-md.font-medium.text-sm.hover:bg-gray-50.transition.cursor-pointer
    {:type "button"
     :on {:click [[:store/assoc-in [:connect3 :username] suggested-username]]}}
    [:i.fas.fa-user-check.mr-2]
    (str "Use @" suggested-username)]])

(defn error-message
  [state]
  (when-let [message (friendly-error-message state)]
    (cond
      (and (authentication-confirmation? state)
           (= (:status state) "login-data")
           (:login-link-sent-to state))
      (login-link-sent-message (:login-link-sent-to state)
                               (:organization/uuid state))

      (and (authentication-confirmation? state)
           (= (:status state) "login-data"))
      authentication-confirmation-message

      (and (= (:class state)
              "InstagramAPI\\Exception\\IncorrectPasswordException")
           (= (:status state)
              "login-data")
           ;; Other parts clear the `:error-message` (but not the `:class`)
           ;; if the error is resolved. Therefore do not show the hints
           ;; below, if the `:error-message` is `nil`.
           (:error-message state))
      (wrong-password-message (:organization/uuid state))

      (= (:error-message state)
         "You must provide a valid 2FA challenge type.")
      (error-card "Choose a different 2FA method"
                  not-supported-challenge-type-message
                  (:organization/uuid state))

      (= (:error-message state) "FIRST_PASSWORD_FAILURE")
      (error-card "Password recently changed?"
                  recently-changed-password-message
                  (:organization/uuid state))

      (and (= (:class state) identification-error-class)
           (:error-message state)
           (:suggested-username state))
      (error-card "Check the username"
                  (username-suggestion (:suggested-username state))
                  (:organization/uuid state))

      :else
      (error-card (error-title state) message (:organization/uuid state)))))
